<script setup>
import LoadingSpinner from "@js/components/LoadingSpinner.vue";
import DataTableLayout from "@js/components/tables/DataTableLayout.vue";
import CrossIconCell from "@js/components/tables/cells/CrossIconCell.vue";
import PercentageCell from "@js/components/tables/cells/PercentageCell.vue";
import PriceCellV2 from "@js/components/tables/cells/PriceCellV2.vue";
import TextCellV2 from "@js/components/tables/cells/TextCellV2.vue";
import { useDataTable } from "@js/utils/dataTable";
import { useRoute } from "@js/utils/route";
import { createColumnHelper } from "@tanstack/vue-table";
import { h } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const { userSessionKey, type } = defineProps({
  userSessionKey: {
    type: String,
    required: false,
  },
  type: {
    type: String,
    required: false,
  },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
  loading,
  table,
  count,
  globalFilter,
  filters,
  availableFilters,
  setGlobalFilter,
  setFilters,
} = useDataTable({
  columns: [
    columnHelper.accessor(() => null, {
      id: "name",
      header: () => t("label.name"),
      cell: (props) =>
        h(TextCellV2, {
          text: props.row.original.name,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "lastYearTurnoverExcludingTax",
      header: () => t("label.lastYearTurnoverExcludingTax"),
      cell: (props) =>
        props.row.original.lastYear.turnoverExcludingTax
          ? h(PriceCellV2, {
              price: props.row.original.lastYear.turnoverExcludingTax,
              withDecimals: false,
            })
          : h(CrossIconCell),
      enableSorting: false,
    }),
    columnHelper.accessor(() => null, {
      id: "lastYearApikomCost",
      header: () => t("label.lastYearApikomCost"),
      cell: (props) =>
        props.row.original.apikomCostLabel
          ? h(TextCellV2, { text: props.row.original.apikomCostLabel })
          : props.row.original.lastYear.apikomCost
            ? h(PriceCellV2, {
                price: props.row.original.lastYear.apikomCost,
                withDecimals: false,
              })
            : h(CrossIconCell),
      enableSorting: false,
    }),
    columnHelper.accessor(() => null, {
      id: "lastYearApikomCostToTurnoverRatio",
      header: () => t("label.lastYearApikomCostToTurnoverRatio"),
      cell: (props) =>
        props.row.original.lastYear.apikomCostToTurnoverRatio
          ? h(PercentageCell, {
              percentage: props.row.original.lastYear.apikomCostToTurnoverRatio,
              withDecimals: true,
              type:
                props.row.original.currentYear.apikomCostToTurnoverRatio > 5
                  ? "danger"
                  : "success",
            })
          : h(CrossIconCell),
      enableSorting: false,
    }),
    columnHelper.accessor(() => null, {
      id: "lastYearSeaAndSmaCostToTurnoverRatio",
      header: () => t("label.lastYearSeaAndSmaCostToTurnoverRatio"),
      cell: (props) =>
        props.row.original.lastYear.seaAndSmaCostToTurnoverRatio
          ? h(PercentageCell, {
            percentage:
            props.row.original.lastYear.seaAndSmaCostToTurnoverRatio,
            withDecimals: true,
          })
          : h(CrossIconCell),
      enableSorting: false,
    }),
    columnHelper.accessor(() => null, {
      id: "currentYearTurnoverExcludingTax",
      header: () => t("label.currentYearTurnoverExcludingTax"),
      cell: (props) =>
        props.row.original.currentYear.turnoverExcludingTax
          ? h(PriceCellV2, {
              price: props.row.original.currentYear.turnoverExcludingTax,
              withDecimals: false,
            })
          : h(CrossIconCell),
      enableSorting: false,
    }),
    columnHelper.accessor(() => null, {
      id: "currentYearApikomCost",
      header: () => t("label.currentYearApikomCost"),
      cell: (props) =>
        props.row.original.apikomCostLabel
          ? h(TextCellV2, { text: props.row.original.apikomCostLabel })
          : props.row.original.currentYear.apikomCost
            ? h(PriceCellV2, {
                price: props.row.original.currentYear.apikomCost,
                withDecimals: false,
              })
            : h(CrossIconCell),
      enableSorting: false,
    }),
    columnHelper.accessor(() => null, {
      id: "currentYearApikomCostToTurnoverRatio",
      header: () => t("label.currentYearApikomCostToTurnoverRatio"),
      cell: (props) =>
        props.row.original.currentYear.apikomCostToTurnoverRatio
          ? h(PercentageCell, {
              percentage:
                props.row.original.currentYear.apikomCostToTurnoverRatio,
              withDecimals: true,
              type:
                props.row.original.currentYear.apikomCostToTurnoverRatio > 5
                  ? "danger"
                  : "success",
            })
          : h(CrossIconCell),
      enableSorting: false,
    }),
    columnHelper.accessor(() => null, {
      id: "currentYearSeaAndSmaCostToTurnoverRatio",
      header: () => t("label.currentYearSeaAndSmaCostToTurnoverRatio"),
      cell: (props) =>
        props.row.original.currentYear.seaAndSmaCostToTurnoverRatio
          ? h(PercentageCell, {
            percentage:
            props.row.original.currentYear.seaAndSmaCostToTurnoverRatio,
            withDecimals: true,
          })
          : h(CrossIconCell),
      enableSorting: false,
    }),
  ],
  url: route("front.projectManagement.apikom.postOpportunities"),
  userSessionKey,
  data: {
    type,
  },
});
</script>

<template>
  <LoadingSpinner v-if="loading" />

  <div v-else>
    <DataTableLayout
      :table="table"
      :count="count"
      :global-filter="globalFilter"
      :filters="filters"
      :available-filters="availableFilters"
      :set-filters="setFilters"
      :set-global-filter="setGlobalFilter"
    />
  </div>
</template>
